// src/state/project.ts (T5; T6 consumes read-only): one store per project (spec 2.5).
// getProjectStore(pid) talks to the real API: it loads the snapshot, then polls the project's version counter
// (/updates) every 15 s, or every 4 s while the thread or a sort batch is on screen, and stops while the tab is
// hidden. Mutations update the signals first where the spec marks them optimistic and roll back on an error.
// createFixtureStore(snapshot) is the same store with no network: every mutation applies locally.
import { batch, computed, signal, type ReadonlySignal, type Signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import { POLL_MS } from "../../shared/constants";
import type {
  AddRoomRequest, ChoiceAnswer, Comment, CreateInviteRequest, CreateInviteResponse, FactsPatch, FileItemsRequest, Item, Message,
  MessageFile, NotifyMode, PostMessageRequest, PresenceRow, ProjectSnapshot, ProjectUpdate, Reaction, ReactionRow, Room,
  RoomReflection, UpdateItemRequest, UpdateMemberRequest, UpdateRoomRequest,
} from "../../shared/api";
import { CHOICE_QUESTIONS } from "../../shared/choices";
import { api } from "../lib/api";
import { ApiError } from "../lib/http";
import { signedOut } from "./session";

export type ProjectStore = {
  pid: string;
  status: ReadonlySignal<"loading" | "ready" | "error" | "gone">;
  error: ReadonlySignal<ApiError | null>;
  snapshot: ReadonlySignal<ProjectSnapshot | null>;
  // derived
  roomsById: ReadonlySignal<Map<string, Room>>;
  itemsByRoom: ReadonlySignal<Map<string | null, Item[]>>;      // null key = unsorted photos
  reactionsByItem: ReadonlySignal<Map<string, ReactionRow[]>>;
  commentsByTarget: ReadonlySignal<Map<string, Comment[]>>;     // key `${targetType}:${targetId}`
  unread: ReadonlySignal<number>;
  presence: ReadonlySignal<PresenceRow[]>;
  actingAs: Signal<string>;                                     // user id; defaults to viewer; only values in snapshot.viewer.canActAs
  // lifecycle
  start(): void;                     // loads snapshot, starts poller
  stop(): void;                      // stops poller (refcounted; last stop stops)
  refresh(): Promise<void>;
  setView(v: { roomId?: string | null; thread?: boolean; sortingBatch?: string | null }): void; // drives poll speed + presence
  // mutations: each resolves after the server confirms; optimistic ones update signals first and roll back on error
  react(itemId: string, value: Reaction | null): Promise<void>;                          // optimistic
  choose(questionId: string, value: string): Promise<void>;                             // optimistic (Big Choices)
  addComment(target: { type: "room" | "item"; id: string }, body: string): Promise<Comment>;
  editComment(commentId: string, body: string): Promise<void>;
  deleteComment(commentId: string): Promise<void>;                                       // optimistic
  saveRoomAnswer(roomId: string, text: string): Promise<void>;
  addRoom(input: AddRoomRequest): Promise<Room>;
  updateRoom(roomId: string, patch: UpdateRoomRequest): Promise<void>;
  deleteRoom(roomId: string): Promise<void>;
  updateItem(itemId: string, patch: UpdateItemRequest): Promise<void>;                   // optimistic for roomId/title/note
  fileItems(entries: FileItemsRequest["entries"]): Promise<void>;                        // optimistic
  deletePhoto(itemId: string): Promise<void>;                                            // optimistic, undo via restorePhoto
  restorePhoto(itemId: string): Promise<void>;
  sendMessage(input: PostMessageRequest, files?: MessageFile[]): Promise<Message>;       // optimistic "sending" row (with the files it names)
  loadOlderMessages(): Promise<void>;
  markRead(lastReadId: number): Promise<void>;                                           // debounced 1 s
  chooseTime(messageId: number, index: number): Promise<void>;
  updateFacts(facts: FactsPatch): Promise<void>;
  requestReflection(roomId: string): Promise<void>;
  reflectionFeedback(roomId: string, value: "yes" | "not_quite", note?: string): Promise<void>;
  invite(input: CreateInviteRequest): Promise<CreateInviteResponse>;
  resendInvite(inviteId: string): Promise<void>;
  revokeInvite(inviteId: string): Promise<void>;
  removeMember(userId: string): Promise<void>;
  updateMember(userId: string, patch: UpdateMemberRequest): Promise<void>;
  setMyNotify(mode: NotifyMode): Promise<void>;
};

/** The subset of lib/api.ts the store uses; tests pass a fake. */
export type ProjectClient = Pick<
  typeof api,
  | "project" | "updates" | "setReaction" | "setChoice" | "addComment" | "editComment" | "deleteComment" | "addRoom" | "updateRoom" | "deleteRoom"
  | "updateItem" | "fileItems" | "deletePhoto" | "restorePhoto" | "postMessage" | "messagesPage" | "markRead" | "chooseTime"
  | "updateFacts" | "requestReflection" | "reflectionFeedback" | "createInvite" | "resendInvite" | "revokeInvite" | "removeMember"
  | "updateMember" | "updateMyMembership"
>;

/**
 * A limited session tried a sensitive action (403 `reauth_required`, spec 2.6): the prospect shell shows the
 * "confirm it's you" sheet while this is set. Cleared when the sheet closes.
 */
export const reauthPrompt: Signal<{ pid: string } | null> = signal(null);

/** Rooms whose reflection is being requested right now, keyed `${pid}:${roomId}` (for the "Updating" state). */
export const reflectionPending: Signal<ReadonlySet<string>> = signal(new Set<string>());

const stores = new Map<string, ProjectStore>();
const writers = new WeakMap<ProjectStore, (items: readonly Item[], version?: number) => void>();

/** Folds items created outside the store (photo uploads, state/uploads.ts) into its snapshot. */
export function ingestItems(store: ProjectStore, items: readonly Item[], version?: number): void {
  writers.get(store)?.(items, version);
}

/** One store per pid, cached in a module-level Map. Components call useProject(pid) which start()s on mount and stop()s on unmount. */
export function getProjectStore(pid: string): ProjectStore {
  let s = stores.get(pid);
  if (!s) {
    s = createApiStore(pid);
    stores.set(pid, s);
  }
  return s;
}

export function useProject(pid: string): ProjectStore {
  const store = getProjectStore(pid);
  useEffect(() => {
    store.start();
    return () => store.stop();
  }, [store]);
  return store;
}

/** Forget cached stores (sign out, tests). */
export function resetProjectStores(): void {
  for (const s of stores.values()) s.stop();
  stores.clear();
}

/** Fixture mode: a store backed by an in-memory ProjectSnapshot. Mutations apply locally and never call the API.
 *  Used by T5's and T6's component tests and by T6 before T3's API lands. */
export function createFixtureStore(snapshot: ProjectSnapshot): ProjectStore {
  return buildStore(snapshot.project.id, null, snapshot);
}

/** The API-backed store (exported for tests, which pass a fake client). */
export function createApiStore(pid: string, client: ProjectClient = api): ProjectStore {
  return buildStore(pid, client, null);
}

// ------------------------------------------------------------------ pure reducers (shared by both modes)

const nowIso = () => new Date().toISOString();
let tempSeq = 0;

function maxId<T extends { id: number }>(rows: readonly T[]): number {
  let m = 0;
  for (const r of rows) if (r.id > m) m = r.id;
  return m;
}

/** Big Choices: one answer set locally, in question order (the server's order, shared/choices.ts). */
function setChoiceLocal(s: ProjectSnapshot, questionId: string, value: string, chosenBy: string, actedBy: string | null): ProjectSnapshot {
  const row: ChoiceAnswer = { questionId, value, chosenBy, actedBy, updatedAt: nowIso() };
  const rest = s.choices.filter((c) => c.questionId !== questionId);
  const order = CHOICE_QUESTIONS.map((q) => q.id);
  return { ...s, choices: [...rest, row].sort((a, b) => order.indexOf(a.questionId) - order.indexOf(b.questionId)) };
}

function withItemReactions(s: ProjectSnapshot, itemId: string, rows: ReactionRow[]): ProjectSnapshot {
  return { ...s, reactions: [...s.reactions.filter((r) => r.itemId !== itemId), ...rows] };
}

function setReactionLocal(s: ProjectSnapshot, itemId: string, userId: string, actedBy: string | null, value: Reaction | null): ProjectSnapshot {
  const rest = s.reactions.filter((r) => !(r.itemId === itemId && r.userId === userId));
  if (!value) return { ...s, reactions: rest };
  return { ...s, reactions: [...rest, { itemId, userId, value, source: "user", actedBy, updatedAt: nowIso() }] };
}

function replaceItems(s: ProjectSnapshot, items: readonly Item[]): ProjectSnapshot {
  const byId = new Map(items.map((i) => [i.id, i]));
  const known = new Set(s.items.map((i) => i.id));
  return { ...s, items: [...s.items.map((i) => byId.get(i.id) ?? i), ...items.filter((i) => !known.has(i.id))] };
}

function patchItemLocal(s: ProjectSnapshot, itemId: string, patch: UpdateItemRequest): ProjectSnapshot {
  return {
    ...s,
    items: s.items.map((i) => {
      if (i.id !== itemId) return i;
      const next = { ...i } as Item;
      if (patch.hidden !== undefined) next.hidden = patch.hidden;
      if (patch.roomId !== undefined) {
        next.roomId = patch.roomId;
        if (next.kind === "photo") next.filedAt = patch.roomId ? nowIso() : null;
      }
      if (next.kind === "photo") {
        if (patch.title !== undefined) next.title = patch.title;
        if (patch.note !== undefined) next.note = patch.note;
      }
      return next;
    }),
  };
}

function fileLocal(s: ProjectSnapshot, entries: FileItemsRequest["entries"]): ProjectSnapshot {
  const by = new Map(entries.map((e) => [e.itemId, e]));
  return {
    ...s,
    items: s.items.map((i) => {
      const e = by.get(i.id);
      if (!e || i.kind !== "photo") return i;
      return { ...i, roomId: e.roomId, filedAt: e.roomId ? nowIso() : null, note: e.note !== undefined && e.note !== "" ? e.note : i.note };
    }),
  };
}

function setDeletedLocal(s: ProjectSnapshot, itemId: string, deletedAt: string | null): ProjectSnapshot {
  return { ...s, items: s.items.map((i) => (i.id === itemId && i.kind === "photo" ? { ...i, deletedAt } : i)) };
}

function upsertRoom(s: ProjectSnapshot, room: Room): ProjectSnapshot {
  const has = s.rooms.some((r) => r.id === room.id);
  const rooms = has ? s.rooms.map((r) => (r.id === room.id ? room : r)) : [...s.rooms, room];
  return { ...s, rooms: [...rooms].sort((a, b) => a.sort - b.sort) };
}

function patchRoomLocal(s: ProjectSnapshot, roomId: string, patch: UpdateRoomRequest, userId: string): ProjectSnapshot {
  return {
    ...s,
    rooms: s.rooms.map((r) => {
      if (r.id !== roomId) return r;
      const next = { ...r };
      if (patch.name !== undefined) next.name = patch.name;
      if (patch.status !== undefined) next.status = patch.status;
      if (patch.sort !== undefined) next.sort = patch.sort;
      if (patch.answer !== undefined) next.answer = { text: patch.answer, updatedBy: userId, updatedAt: nowIso() };
      return next;
    }),
  };
}

function upsertReflection(s: ProjectSnapshot, r: RoomReflection): ProjectSnapshot {
  return { ...s, reflections: [...s.reflections.filter((x) => x.roomId !== r.roomId), r] };
}

/** Folds an /updates answer into the local snapshot: everything is replaced except the thread and activity, which grow. */
export function mergeUpdate(s: ProjectSnapshot, u: Extract<ProjectUpdate, { changed: true }>): ProjectSnapshot {
  const { changed: _c, messagesAfter, messagesUpdated, activityAfter, ...rest } = u;
  void _c;
  const confirmed = s.messages.filter((m) => !m.pending);
  const pending = s.messages.filter((m) => m.pending);
  const known = new Set(confirmed.map((m) => m.id));
  const updated = new Map(messagesUpdated.map((m) => [m.id, m]));
  const messages = [...confirmed.map((m) => updated.get(m.id) ?? m), ...messagesAfter.filter((m) => !known.has(m.id))].sort((a, b) => a.id - b.id);
  const seen = new Set(s.activity.map((a) => a.id));
  const activity = [...s.activity, ...activityAfter.filter((a) => !seen.has(a.id))].sort((a, b) => a.id - b.id).slice(-200);
  return { ...s, ...rest, messages: [...messages, ...pending], activity, hasOlderMessages: s.hasOlderMessages };
}

// ------------------------------------------------------------------ the store

type View = { roomId: string | null; thread: boolean; sortingBatch: string | null };

function buildStore(pid: string, client: ProjectClient | null, initial: ProjectSnapshot | null): ProjectStore {
  const snapshot = signal<ProjectSnapshot | null>(initial);
  const status = signal<"loading" | "ready" | "error" | "gone">(initial ? "ready" : "loading");
  const error = signal<ApiError | null>(null);
  const actingAs = signal(initial?.viewer.userId ?? "");
  const view: View = { roomId: null, thread: false, sortingBatch: null };
  let refs = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let inflight = 0;
  let loading: Promise<void> | null = null;
  let readTimer: ReturnType<typeof setTimeout> | null = null;
  let readTarget = 0;
  const readWaiters: (() => void)[] = [];
  let listening = false;

  const snap = (): ProjectSnapshot => {
    const s = snapshot.value;
    if (!s) throw new ApiError(0, "network", "The board has not loaded yet.");
    return s;
  };
  const update = (fn: (s: ProjectSnapshot) => ProjectSnapshot) => {
    const s = snapshot.value;
    if (s) snapshot.value = fn(s);
  };
  const viewerId = () => snapshot.value?.viewer.userId ?? "";
  /** "Mutation responses that carry version update the local version only when it equals local + 1." */
  const bumpVersion = (v: number | undefined) => {
    const s = snapshot.value;
    if (!s || typeof v !== "number") return;
    if (v === s.project.version + 1) snapshot.value = { ...s, project: { ...s.project, version: v } };
  };

  // ---- derived
  const roomsById = computed(() => new Map((snapshot.value?.rooms ?? []).map((r) => [r.id, r])));
  const itemsByRoom = computed(() => {
    const m = new Map<string | null, Item[]>();
    for (const it of snapshot.value?.items ?? []) {
      if (it.kind === "photo" && it.deletedAt) continue;
      const list = m.get(it.roomId) ?? [];
      list.push(it);
      m.set(it.roomId, list);
    }
    return m;
  });
  const reactionsByItem = computed(() => {
    const m = new Map<string, ReactionRow[]>();
    for (const r of snapshot.value?.reactions ?? []) {
      const list = m.get(r.itemId) ?? [];
      list.push(r);
      m.set(r.itemId, list);
    }
    return m;
  });
  const commentsByTarget = computed(() => {
    const m = new Map<string, Comment[]>();
    for (const c of snapshot.value?.comments ?? []) {
      const key = `${c.targetType}:${c.targetId}`;
      const list = m.get(key) ?? [];
      list.push(c);
      m.set(key, list);
    }
    for (const list of m.values()) list.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    return m;
  });
  const unread = computed(() => {
    const s = snapshot.value;
    if (!s) return 0;
    // The automatic welcome (kind system) is not the team writing: it never counts as unread (round-1 QA).
    return s.messages.filter(
      (m) => !m.pending && m.id > s.reads.myLastReadId && m.visibility === "all" && m.kind !== "inquiry" && m.kind !== "system" && m.authorId !== s.viewer.userId,
    ).length;
  });
  const presence = computed(() => snapshot.value?.presence ?? []);

  // ---- errors: 401 signs out, reauth_required opens the sheet, 404 on the snapshot means gone
  function handle(e: unknown): never {
    if (e instanceof ApiError) {
      if (e.status === 401) signedOut(typeof location !== "undefined" ? location.pathname : undefined);
      if (e.code === "reauth_required") reauthPrompt.value = { pid };
    }
    throw e;
  }

  async function run<T>(fn: () => Promise<T>): Promise<T> {
    inflight++;
    try {
      return await fn();
    } catch (e) {
      return handle(e);
    } finally {
      inflight--;
    }
  }

  // ---- loading and polling
  function setSnapshot(s: ProjectSnapshot) {
    batch(() => {
      snapshot.value = s;
      status.value = "ready";
      error.value = null;
      if (!s.viewer.canActAs.includes(actingAs.value)) actingAs.value = s.viewer.userId;
    });
  }

  async function load(): Promise<void> {
    if (!client) return;
    try {
      const s = await client.project(pid);
      setSnapshot(s);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        status.value = "gone";
        return;
      }
      if (e instanceof ApiError && e.status === 401) {
        signedOut(typeof location !== "undefined" ? location.pathname : undefined);
        return;
      }
      error.value = e instanceof ApiError ? e : new ApiError(0, "network", "We couldn't load this board.");
      if (!snapshot.value) status.value = "error";
    }
  }

  function interval(): number {
    return view.thread || view.sortingBatch ? POLL_MS.thread : POLL_MS.idle;
  }
  function hidden(): boolean {
    return typeof document !== "undefined" && document.visibilityState === "hidden";
  }
  function clearTimer() {
    if (timer) clearTimeout(timer);
    timer = null;
  }
  function schedule(ms = interval()) {
    clearTimer();
    if (!client || refs === 0 || hidden()) return;
    timer = setTimeout(() => void poll(), ms);
  }

  async function poll(): Promise<void> {
    timer = null;
    if (!client || refs === 0) return;
    const s = snapshot.value;
    if (!s) {
      await load();
      schedule();
      return;
    }
    try {
      const confirmed = s.messages.filter((m) => !m.pending);
      const u = await client.updates(pid, { v: s.project.version, m: maxId(confirmed), a: maxId(s.activity), room: view.roomId ?? undefined });
      // A mutation in flight would be overwritten by an older server state: skip this round, the next poll reconciles.
      if (inflight === 0 && snapshot.value === s) {
        if (u.changed) setSnapshot(mergeUpdate(s, u));
        else if (u.presence.length) snapshot.value = { ...s, presence: u.presence };
      }
      if (error.value) error.value = null;
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        status.value = "gone";
        return;
      }
      if (e instanceof ApiError && e.status === 401) {
        signedOut(typeof location !== "undefined" ? location.pathname : undefined);
        return;
      }
      error.value = e instanceof ApiError ? e : new ApiError(0, "network", "We couldn't reach the server.");
    }
    schedule();
  }

  function onVisibility() {
    if (refs === 0) return;
    if (hidden()) clearTimer();
    else schedule(0);
  }

  function refresh(): Promise<void> {
    if (!client) return Promise.resolve();
    loading ??= load().finally(() => {
      loading = null;
    });
    return loading;
  }

  // ---- mutations
  const asUser = () => {
    const a = actingAs.value;
    return a && a !== viewerId() ? a : undefined;
  };

  const store: ProjectStore = {
    pid,
    status,
    error,
    snapshot,
    roomsById,
    itemsByRoom,
    reactionsByItem,
    commentsByTarget,
    unread,
    presence,
    actingAs,

    start() {
      refs++;
      if (refs > 1 || !client) return;
      if (!listening && typeof document !== "undefined") {
        document.addEventListener("visibilitychange", onVisibility);
        listening = true;
      }
      if (!snapshot.value) void refresh().then(() => schedule());
      else schedule(0);
    },
    stop() {
      if (refs === 0) return;
      refs--;
      if (refs > 0) return;
      clearTimer();
      if (listening && typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
        listening = false;
      }
    },
    refresh,
    setView(v) {
      const wasFast = view.thread || view.sortingBatch;
      if (v.roomId !== undefined) view.roomId = v.roomId;
      if (v.thread !== undefined) view.thread = v.thread;
      if (v.sortingBatch !== undefined) view.sortingBatch = v.sortingBatch;
      const isFast = view.thread || view.sortingBatch;
      if (timer && Boolean(wasFast) !== Boolean(isFast)) schedule();
    },

    async react(itemId, value) {
      const s = snap();
      const userId = actingAs.value || s.viewer.userId;
      const actedBy = userId === s.viewer.userId ? null : s.viewer.userId;
      const before = s.reactions.filter((r) => r.itemId === itemId);
      update((x) => setReactionLocal(x, itemId, userId, actedBy, value));
      if (!client) return;
      try {
        const res = await run(() => client.setReaction(pid, itemId, { value, asUserId: asUser() }));
        update((x) => withItemReactions(x, itemId, res.reactions));
        bumpVersion(res.version);
      } catch (e) {
        update((x) => withItemReactions(x, itemId, before));
        throw e;
      }
    },

    async choose(questionId, value) {
      const s = snap();
      const userId = actingAs.value || s.viewer.userId;
      const actedBy = userId === s.viewer.userId ? null : s.viewer.userId;
      const before = s.choices;
      update((x) => setChoiceLocal(x, questionId, value, userId, actedBy));
      if (!client) return;
      try {
        const res = await run(() => client.setChoice(pid, questionId, { value, asUserId: asUser() }));
        update((x) => ({ ...x, choices: res.choices }));
        bumpVersion(res.version);
      } catch (e) {
        update((x) => ({ ...x, choices: before }));
        throw e;
      }
    },

    async addComment(target, body) {
      const s = snap();
      const userId = actingAs.value || s.viewer.userId;
      if (!client) {
        const comment: Comment = {
          id: `cmt_fixture${(++tempSeq).toString(36)}`, targetType: target.type, targetId: target.id, userId,
          actedBy: userId === s.viewer.userId ? null : s.viewer.userId, body, createdAt: nowIso(), editedAt: null,
        };
        update((x) => ({ ...x, comments: [...x.comments, comment] }));
        return comment;
      }
      const res = await run(() => client.addComment(pid, { targetType: target.type, targetId: target.id, body, asUserId: asUser() }));
      update((x) => ({ ...x, comments: [...x.comments.filter((c) => c.id !== res.comment.id), res.comment] }));
      bumpVersion(res.version);
      return res.comment;
    },

    async editComment(commentId, body) {
      if (!client) {
        update((x) => ({ ...x, comments: x.comments.map((c) => (c.id === commentId ? { ...c, body, editedAt: nowIso() } : c)) }));
        return;
      }
      const res = await run(() => client.editComment(pid, commentId, { body }));
      update((x) => ({ ...x, comments: x.comments.map((c) => (c.id === commentId ? res.comment : c)) }));
      bumpVersion(res.version);
    },

    async deleteComment(commentId) {
      const s = snap();
      const removed = s.comments.find((c) => c.id === commentId);
      update((x) => ({ ...x, comments: x.comments.filter((c) => c.id !== commentId) }));
      if (!client) return;
      try {
        const res = await run(() => client.deleteComment(pid, commentId));
        bumpVersion(res.version);
      } catch (e) {
        if (removed) update((x) => ({ ...x, comments: [...x.comments, removed] }));
        throw e;
      }
    },

    async saveRoomAnswer(roomId, text) {
      await store.updateRoom(roomId, { answer: text });
    },

    async addRoom(input) {
      if (!client) {
        const s = snap();
        const room: Room = {
          id: `rm_fixture${(++tempSeq).toString(36)}`, key: input.suggestionKey ?? "custom", name: input.name ?? input.suggestionKey ?? "Room",
          kicker: null, prompt: null, askFor: null, custom: !input.suggestionKey, sort: Math.max(0, ...s.rooms.map((r) => r.sort)) + 1,
          status: "open", flags: [], answer: { text: "", updatedBy: null, updatedAt: null }, createdAt: nowIso(),
        };
        update((x) => upsertRoom(x, room));
        return room;
      }
      const res = await run(() => client.addRoom(pid, input));
      update((x) => replaceItems(upsertRoom(x, res.room), res.items));
      bumpVersion(res.version);
      return res.room;
    },

    async updateRoom(roomId, patch) {
      const s = snap();
      const before = s.rooms.find((r) => r.id === roomId);
      update((x) => patchRoomLocal(x, roomId, patch, actingAs.value || x.viewer.userId));
      if (!client) return;
      try {
        const res = await run(() => client.updateRoom(pid, roomId, patch));
        update((x) => replaceItems(upsertRoom(x, res.room), res.items));
        bumpVersion(res.version);
      } catch (e) {
        if (before) update((x) => upsertRoom(x, before));
        throw e;
      }
    },

    async deleteRoom(roomId) {
      if (!client) {
        update((x) => ({
          ...x,
          rooms: x.rooms.filter((r) => r.id !== roomId),
          items: x.items.filter((i) => i.kind === "photo" || i.roomId !== roomId).map((i) => (i.roomId === roomId ? { ...i, roomId: null } : i)),
        }));
        return;
      }
      await run(() => client.deleteRoom(pid, roomId));
      await refresh();
    },

    async updateItem(itemId, patch) {
      const s = snap();
      const before = s.items.find((i) => i.id === itemId);
      update((x) => patchItemLocal(x, itemId, patch));
      if (!client) return;
      try {
        const res = await run(() => client.updateItem(pid, itemId, patch));
        update((x) => replaceItems(x, [res.item]));
        bumpVersion(res.version);
      } catch (e) {
        if (before) update((x) => replaceItems(x, [before]));
        throw e;
      }
    },

    async fileItems(entries) {
      const s = snap();
      const ids = new Set(entries.map((e) => e.itemId));
      const before = s.items.filter((i) => ids.has(i.id));
      update((x) => fileLocal(x, entries));
      if (!client) return;
      try {
        const res = await run(() => client.fileItems(pid, { entries }));
        update((x) => replaceItems(x, res.items));
        bumpVersion(res.version);
      } catch (e) {
        update((x) => replaceItems(x, before));
        throw e;
      }
    },

    async deletePhoto(itemId) {
      update((x) => setDeletedLocal(x, itemId, nowIso()));
      if (!client) return;
      try {
        const res = await run(() => client.deletePhoto(pid, itemId));
        update((x) => replaceItems(x, [res.item]));
        bumpVersion(res.version);
      } catch (e) {
        update((x) => setDeletedLocal(x, itemId, null));
        throw e;
      }
    },

    async restorePhoto(itemId) {
      if (!client) {
        update((x) => setDeletedLocal(x, itemId, null));
        return;
      }
      const res = await run(() => client.restorePhoto(pid, itemId));
      update((x) => replaceItems(x, [res.item]));
      bumpVersion(res.version);
    },

    async sendMessage(input, files = []) {
      const s = snap();
      const me = s.members.find((m) => m.userId === s.viewer.userId);
      const temp: Message = {
        id: -(++tempSeq), kind: "text", side: s.viewer.mode === "team" ? "team" : "client", authorId: s.viewer.userId,
        authorLabel: me?.firstName ?? "You", authorRoleLine: null, visibility: input.visibility ?? "all", body: input.body,
        refs: input.refs ?? [], files, times: null, inquiry: null, via: "app", createdAt: nowIso(), pending: true,
      };
      update((x) => ({ ...x, messages: [...x.messages, temp] }));
      if (!client) {
        const done: Message = { ...temp, id: maxId(s.messages) + 1 };
        delete done.pending;
        update((x) => ({ ...x, messages: x.messages.map((m) => (m.id === temp.id ? done : m)) }));
        return done;
      }
      try {
        const res = await run(() => client.postMessage(pid, input));
        update((x) => ({
          ...x,
          messages: [...x.messages.filter((m) => m.id !== temp.id && m.id !== res.message.id), res.message].sort((a, b) =>
            a.pending === b.pending ? a.id - b.id : a.pending ? 1 : -1,
          ),
          reads: { ...x.reads, myLastReadId: Math.max(x.reads.myLastReadId, res.message.id) },
        }));
        bumpVersion(res.version);
        return res.message;
      } catch (e) {
        update((x) => ({ ...x, messages: x.messages.filter((m) => m.id !== temp.id) }));
        throw e;
      }
    },

    async loadOlderMessages() {
      const s = snap();
      if (!client || !s.hasOlderMessages) return;
      const first = s.messages.find((m) => !m.pending);
      if (!first) return;
      const page = await run(() => client.messagesPage(pid, { before: first.id, limit: 50 }));
      update((x) => {
        const known = new Set(x.messages.map((m) => m.id));
        return { ...x, messages: [...page.messages.filter((m) => !known.has(m.id)), ...x.messages], hasOlderMessages: page.hasOlder };
      });
    },

    markRead(lastReadId) {
      const s = snapshot.value;
      if (!s || lastReadId <= s.reads.myLastReadId) return Promise.resolve();
      update((x) => ({ ...x, reads: { ...x.reads, myLastReadId: Math.max(x.reads.myLastReadId, lastReadId) } }));
      if (!client || s.viewer.mode !== "member") return Promise.resolve();
      readTarget = Math.max(readTarget, lastReadId);
      if (readTimer) clearTimeout(readTimer);
      return new Promise<void>((resolve) => {
        readWaiters.push(resolve);
        readTimer = setTimeout(() => {
          readTimer = null;
          const waiters = readWaiters.splice(0);
          const done = () => waiters.forEach((w) => w());
          client.markRead(pid, { lastReadId: readTarget }).then(done, done);
        }, 1000);
      });
    },

    async chooseTime(messageId, index) {
      const s = snap();
      const before = s.messages.find((m) => m.id === messageId);
      if (!before?.times) throw new ApiError(400, "bad_request", "Those times are no longer available.");
      update((x) => ({
        ...x,
        messages: x.messages.map((m) => (m.id === messageId && m.times ? { ...m, times: { ...m.times, chosenIndex: index } } : m)),
      }));
      if (!client) return;
      try {
        const res = await run(() => client.chooseTime(pid, messageId, { index }));
        update((x) => {
          const known = new Set(x.messages.map((m) => m.id));
          const messages = x.messages.map((m) => (m.id === res.proposal.id ? res.proposal : m));
          return { ...x, messages: known.has(res.reply.id) ? messages : [...messages, res.reply] };
        });
        bumpVersion(res.version);
      } catch (e) {
        update((x) => ({ ...x, messages: x.messages.map((m) => (m.id === messageId ? before : m)) }));
        // Someone on the board (or this person on another device) already picked: show their pick.
        if (e instanceof ApiError && e.status === 409) void store.refresh().catch(() => undefined);
        throw e;
      }
    },

    async updateFacts(facts) {
      if (!client) return;
      const res = await run(() => client.updateFacts(pid, { facts }));
      update((x) => ({ ...x, facts: res.facts }));
      bumpVersion(res.version);
    },

    async requestReflection(roomId) {
      if (!client) return;
      const key = `${pid}:${roomId}`;
      if (reflectionPending.value.has(key)) return;
      reflectionPending.value = new Set([...reflectionPending.value, key]);
      try {
        const res = await run(() => client.requestReflection(pid, roomId));
        if (res.reflection) {
          const r = res.reflection;
          update((x) => upsertReflection(x, r));
        }
      } finally {
        const next = new Set(reflectionPending.value);
        next.delete(key);
        reflectionPending.value = next;
      }
    },

    async reflectionFeedback(roomId, value, note) {
      const s = snap();
      const before = s.reflections.find((r) => r.roomId === roomId);
      if (before) update((x) => upsertReflection(x, { ...before, feedback: value }));
      if (!client) return;
      try {
        await run(() => client.reflectionFeedback(pid, roomId, note ? { value, note } : { value }));
      } catch (e) {
        if (before) update((x) => upsertReflection(x, before));
        throw e;
      }
    },

    async invite(input) {
      if (!client) {
        const invite = {
          id: `inv_fixture${(++tempSeq).toString(36)}`, email: input.email, name: input.name, relation: input.relation, firm: input.firm ?? null,
          role: input.relation === "partner" ? ("owner" as const) : ("collaborator" as const), createdAt: nowIso(),
          expiresAt: new Date(Date.now() + 7 * 864e5).toISOString(), status: "pending" as const,
        };
        update((x) => ({ ...x, invites: [...x.invites, invite] }));
        return { invite, link: input.delivery === "link" ? `/auth/continue#t=fixture` : null };
      }
      const res = await run(() => client.createInvite(pid, input));
      update((x) => ({ ...x, invites: [...x.invites.filter((i) => i.id !== res.invite.id), res.invite] }));
      return res;
    },

    async resendInvite(inviteId) {
      if (!client) return;
      await run(() => client.resendInvite(pid, inviteId));
    },

    async revokeInvite(inviteId) {
      const s = snap();
      const removed = s.invites.find((i) => i.id === inviteId);
      update((x) => ({ ...x, invites: x.invites.filter((i) => i.id !== inviteId) }));
      if (!client) return;
      try {
        await run(() => client.revokeInvite(pid, inviteId));
      } catch (e) {
        if (removed) update((x) => ({ ...x, invites: [...x.invites, removed] }));
        throw e;
      }
    },

    async removeMember(userId) {
      if (client) await run(() => client.removeMember(pid, userId));
      update((x) => ({ ...x, members: x.members.filter((m) => m.userId !== userId) }));
    },

    async updateMember(userId, patch) {
      if (!client) {
        update((x) => ({
          ...x,
          members: x.members.map((m) =>
            m.userId === userId
              ? {
                  ...m,
                  relation: patch.relation ?? m.relation,
                  relationNote: patch.relationNote !== undefined ? patch.relationNote : m.relationNote,
                  firm: patch.firm !== undefined ? patch.firm : m.firm,
                }
              : m,
          ),
        }));
        return;
      }
      const res = await run(() => client.updateMember(pid, userId, patch));
      update((x) => ({ ...x, members: x.members.map((m) => (m.userId === userId ? { ...m, ...res.member } : m)) }));
    },

    async setMyNotify(mode) {
      const s = snap();
      const me = s.members.find((m) => m.userId === s.viewer.userId);
      const before = me?.notifyMode;
      const apply = (v: NotifyMode | undefined) =>
        update((x) => ({ ...x, members: x.members.map((m) => (m.userId === x.viewer.userId ? { ...m, notifyMode: v } : m)) }));
      apply(mode);
      if (!client) return;
      try {
        const res = await run(() => client.updateMyMembership(pid, { notifyMode: mode }));
        update((x) => ({ ...x, members: x.members.map((m) => (m.userId === x.viewer.userId ? { ...m, ...res.member } : m)) }));
      } catch (e) {
        apply(before);
        throw e;
      }
    },
  };
  writers.set(store, (items, version) => {
    update((x) => replaceItems(x, items));
    bumpVersion(version);
  });
  return store;
}
